import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../auth/useUser";
import { useAdminAlerts } from "../hooks/useAdminAlerts";
import { alertBody, alertKey, alertTitle, shouldNotifyAdmin } from "../lib/adminAlerts";

// AdminAlertNotifier (0064): mounted once at the App root, as the founder
// app's RunCompletionNotifier is (docs/todos/closed/0027-cross-page-run-awareness.md).
// It watches the open alerts on saved passes and, for each new one, fires a
// browser Notification while the tab is hidden (a visible tab shows the
// header's count). What is already open when the app opens is the baseline:
// it shows as the count, never as a popup. Each alert fires at most once (a
// daily reminder is a new alert and replaces its run's earlier
// notification), and a notification whose alert is no longer open (the pass
// was resumed or closed) is closed. A click opens the run's page, where the
// admin resumes or closes the pass.
export function AdminAlertNotifier() {
  const { data: user } = useUser();
  const { data: alerts } = useAdminAlerts(!!user?.is_admin);
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  // seen: every alert key already handled; null until the first fetch.
  const seen = useRef<Set<string> | null>(null);
  // shown: the notification on screen per run.
  const shown = useRef<Map<string, Notification>>(new Map());

  useEffect(() => {
    if (!alerts) return;
    if (seen.current === null) {
      seen.current = new Set(alerts.map(alertKey));
    } else {
      for (const a of alerts) {
        const key = alertKey(a);
        if (seen.current.has(key)) continue;
        const permission =
          typeof Notification !== "undefined" ? Notification.permission : "denied";
        const fire = shouldNotifyAdmin({
          permission,
          documentHidden: typeof document !== "undefined" && document.hidden,
          alreadyNotified: false,
        });
        if (fire) {
          shown.current.get(a.run_id)?.close();
          shown.current.delete(a.run_id);
          try {
            const n = new Notification(alertTitle(a), { body: alertBody(a), tag: key });
            n.onclick = () => {
              window.focus();
              navigateRef.current(`/users/${a.user_id}/products/${a.product_id}/runs/${a.run_id}`);
              n.close();
            };
            shown.current.set(a.run_id, n);
          } catch {
            // Notification construction can throw on some platforms; ignore.
          }
        }
        // Marked whether or not it fired, so an alert that arrived while the
        // tab was visible never pops up later.
        seen.current.add(key);
      }
    }
    const open = new Set(alerts.map((a) => a.run_id));
    for (const [runId, n] of shown.current) {
      if (!open.has(runId)) {
        n.close();
        shown.current.delete(runId);
      }
    }
  }, [alerts]);

  return null;
}
